import { GET_PRIZES } from 'constants/api'
import useAxios, { RefetchOptions } from 'axios-hooks'
import { useSession } from 'contexts/session-context'
import { createContext, Dispatch, FunctionComponent, useContext, useEffect, useReducer } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import useRouter from 'hooks/use-router'
import { Prize } from '../types'
import prizesReducer, { PrizesAction, PrizesState } from './prizes-reducer'

export interface PrizesContextProps {
  state: PrizesState
  dispatch: Dispatch<PrizesAction>
  refetch: (config?: AxiosRequestConfig, options?: RefetchOptions) => AxiosPromise<Prize[]>
}

const PrizesContext = createContext<PrizesContextProps>({} as PrizesContextProps)

export const PrizesProvider: FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(prizesReducer, {})
  const { setLoad } = useContext(LoaderContext)
  const [{ data: session }] = useSession()
  const { push } = useRouter()
  const [{ data = [], loading, error }, refetch] = useAxios<Prize[]>(
    {
      url: GET_PRIZES,
      headers: { Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}` } 
    },
    { useCache: false }
  )
  useEffect(() => {
    setLoad(loading)
    return () => {
      setLoad(false)
    }
  }, [loading])

  useEffect(() => {
    if (data.length > 0) {
      dispatch({ type: 'SET_PRIZES', payload: data })
    }
  }, [data])

  useEffect(() => {
    if (error) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.code })
      return push('/prizes/error')
    }
  }, [error])

  return <PrizesContext.Provider value={{ state, dispatch, refetch }}>{children}</PrizesContext.Provider>
}

export const usePrizes = () => {
  const { state, dispatch, refetch } = useContext(PrizesContext)
  const { push } = useRouter()

  const setPrizes = (prizes: Prize[]) => dispatch({ type: 'SET_PRIZES', payload: prizes })
  const refetchPrizes = (config?: AxiosRequestConfig) => refetch(config)
  const setSelectPrize = (prize?: Prize) => dispatch({ type: 'SET_SELECT_PRIZE', payload: prize })
  const setCongrats = (congrats?: boolean) => dispatch({ type: 'SET_CONGRATS', payload: congrats })
  const setError = (error: number) => {
    dispatch({ type: 'SET_ERROR', payload: error })
    if (error) return push('/prizes/error')
  }
  const actions = { setPrizes, refetchPrizes, setSelectPrize, setError, setCongrats }

  const returnValue: [typeof state, typeof actions] = [state, actions]
  return returnValue
}
